/** What the Scope 3 page may say, as pure functions (gate 19).
 *
 * There is **no Scope 3 read endpoint**. Everything below, down to the write, is derived from two
 * reads that already exist: `GET /inventory` (its `scope3` coverage: fifteen categories and a relevance decision for each)
 * and `GET /figures?scope=3` (calculated figures, which carry the engine's category and **no
 * Scope 3 category number**). That second fact decides the design: a figure cannot honestly be put
 * against category 6 or category 3 on this screen, because nothing the server says attributes it
 * there — and mapping an engine category to a Scope 3 category is a methodology fact that no row
 * of the verification log (S-08 lists the fifteen names and nothing about what falls under them)
 * supports. So categories show a **decision**, figures are listed **beside** them, and no sum of
 * either is ever formed.
 *
 * **The write (S18) is the one thing here that is not derived**: a person's decision about a
 * category, sent to `PUT /scope3/categories/{n}/relevance` and typed by hand at the bottom of this
 * file until the generated schema has the route.
 */

import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { instantStamp } from "../lib/format";
import type { Reader } from "../lib/format";
import { api, apiFetch } from "./client";
import type { Figure } from "./queries";
import { Refused, refusalFrom } from "./refusal";
import type { components } from "./schema";

/** One category and where the company has got to with it.
 *
 * **`decided_by` and `decided_at` are declared here by hand (S25)** because `schema.d.ts` predates
 * them. They are optional on purpose - the generated type will gain them as required-or-null on the
 * next `make api-types`, and the intersection below then collapses to the generated one without a
 * line changing. Until then a response that omits them reads as "nobody", which is what an older
 * server meant.
 */
export type Scope3Category = components["schemas"]["Scope3CategoryOut"] & {
  /** The person who decided - an identifier, never a name (rule 4). `null` for a category nobody
   * has decided, a person since erased, or a row written with no person at all. */
  decided_by?: string | null;
  decided_at?: string | null;
};
export type Scope3Coverage = components["schemas"]["Scope3CoverageOut"];

/** The words for each relevance state the server can return (`Relevance`, five members).
 *
 * **An unknown value is shown as itself**, never mapped to "Not assessed": a sixth state added on
 * the server must be visibly unfamiliar, because defaulting it to the one state that means *nobody
 * has looked* would assert something about the company that the server did not.
 */
const RELEVANCE_LABEL: Readonly<Record<string, string>> = {
  not_assessed: "Not assessed",
  relevant: "Relevant",
  not_applicable: "Not applicable",
  excluded: "Excluded",
  reported: "Reported",
};

export function relevanceLabel(relevance: string): string {
  return RELEVANCE_LABEL[relevance] ?? relevance;
}

/** The categories in the standard's own order, whatever order they arrived in. */
export function inOrder(categories: readonly Scope3Category[]): Scope3Category[] {
  return [...categories].sort((a, b) => a.number - b.number);
}

/** The standard has fifteen (Table 5.3, verification log S-08). */
export const EXPECTED_CATEGORIES = 15;

/** Whether a returned census holds each category from 1 to 15 exactly once. **A count of rows is not
 * that**: fifteen rows with a duplicate and a gap would certify fifteen by length. */
export function scope3CensusIsValid(categories: readonly Pick<Scope3Category, "number">[]): boolean {
  return categories.length === EXPECTED_CATEGORIES && new Set(categories.map((category) => category.number)).size === EXPECTED_CATEGORIES
    && categories.every((category) => Number.isInteger(category.number) && category.number >= 1 && category.number <= EXPECTED_CATEGORIES);
}


/** Figures a reader may be shown, and the ones rule 3 withholds.
 *
 * **A figure whose receipt cannot be produced is not shown at all** — `receipt_is_complete` is the
 * server's answer to whether all six links hold. The withheld ones are *counted* (a count is not a
 * figure) so that the page does not read as an inventory with fewer lines than it has, and their
 * first broken link is kept so the sentence can say what to fix.
 */
export function splitByReceipt(figures: readonly Figure[]): {
  shown: Figure[];
  withheld: Figure[];
} {
  return {
    shown: figures.filter((f) => f.receipt_is_complete),
    withheld: figures.filter((f) => !f.receipt_is_complete),
  };
}

// ------------------------------------------------------------------------------ the write (S18)

/** The decisions a person can make, in the order they are offered.
 *
 * **Four, not five.** `reported` is the server's word for *a quantified figure for this category is
 * in the inventory*, and nothing attributes a figure to a category, so the server refuses a person
 * who sends it (`reported_is_not_a_decision`). It is not offered here because a control that
 * predictably fails is a defect, not a safeguard; it is still a state the page can *show*, which is
 * why `RELEVANCE_LABEL` above keeps it.
 *
 * **`not_assessed` is a retraction, not a fourth kind of answer.** It is offered only where a
 * decision exists to withdraw.
 */
export type DecidableRelevance = "relevant" | "not_applicable" | "excluded" | "not_assessed";

/** What a person can choose between. The words are the product's own names for its states
 * (`Relevance` on the server) and nothing here says what makes a category relevant: that is not in
 * the verification log, so it is not this page's to suggest (rule 2). */
export const DECISIONS: readonly {
  value: Exclude<DecidableRelevance, "not_assessed">;
  label: string;
  means: string;
}[] = [
  {
    value: "relevant",
    label: "Relevant",
    means: "In scope and still to be quantified. It stays unreported until a figure is.",
  },
  {
    value: "not_applicable",
    label: "Not applicable",
    means: "The company has no such activity. A reason is required.",
  },
  {
    value: "excluded",
    label: "Excluded",
    means: "Relevant, and left out on purpose. A reason is required.",
  },
];

/** Whether the reason is part of this answer. **The server's rule, restated so the form can ask for
 * the right thing** - it refuses a reason without an exclusion and an exclusion without a reason,
 * and a form that collected the wrong one would meet that refusal every time. */
export function needsReason(relevance: DecidableRelevance | ""): boolean {
  return relevance === "excluded" || relevance === "not_applicable";
}

/** Who may record a relevance decision: an Approver or an Admin (`MAY_DECIDE_SCOPE3_RELEVANCE` on
 * the server). `undefined` while the session loads, so a control is withheld rather than a sentence
 * flashed at somebody who may be allowed. */
export function mayDecide(role: string | undefined): boolean | undefined {
  if (role === undefined) return undefined;
  return role === "approver" || role === "admin";
}

/** Whether the window's end is the end of a reporting period this workspace declared - the only
 * kind of year the server accepts a decision for. A custom range is not one, and the screen says
 * so instead of offering a control that predictably fails. */
export function isDeclaredYear(
  periods: readonly { end_date: string }[] | undefined,
  yearEnd: string,
): boolean {
  return (periods ?? []).some((period) => period.end_date === yearEnd);
}

export interface Scope3Decision {
  category: Scope3Category;
  /** True when the answer was already on record and the request wrote nothing. */
  already_recorded: boolean;
}

/** `PUT /api/v1/scope3/categories/{n}/relevance`, **typed by hand**.
 *
 * The generated `schema.d.ts` was produced before this route existed (the owner regenerates once,
 * at integration), so `api.PUT(...)` would not compile. `apiFetch` is used instead and the shapes
 * above stand in for the generated ones. They are checked at the boundary rather than cast: a body
 * that is not this shape is a `TypeError`, which the write-failed rendering treats as an unknown
 * outcome rather than as success.
 *
 * **The tripwire that stood here has fired and been removed** (2026-10-23): `make api-types` added
 * the route, the constant stopped compiling, and whoever ran it was sent here — which is the whole
 * argument for writing an assertion rather than a `TODO`. `DECIDE_PATH` survives it because the
 * runtime boundary check below is still worth having: generated types are a compile-time promise
 * about what the server *should* send, and `readDecision` is what happens when it does not.
 */
export const DECIDE_PATH = "/api/v1/scope3/categories/{category_number}/relevance" as const;


function readDecision(body: unknown): Scope3Decision {
  if (body && typeof body === "object" && "category" in body && "already_recorded" in body) {
    const { category, already_recorded } = body;
    if (
      typeof already_recorded === "boolean" &&
      category &&
      typeof category === "object" &&
      "number" in category &&
      typeof category.number === "number" &&
      "relevance" in category &&
      typeof category.relevance === "string"
    ) {
      return { category: category as Scope3Category, already_recorded };
    }
  }
  throw new TypeError("The decision's response could not be read.");
}

/** What the reader was looking at when they decided: the category's `version`, or `null` for a
 * category with no row on record.
 *
 * **`null` is an answer, not a blank** (ADR-0054, item 1): it says *"I saw no decision"*, and the
 * server refuses it if one has since been recorded. A response that omits the field entirely (an
 * older server) reads as `null` too, which is the failing-safe direction: a row that does exist is
 * then a stale expectation and is refused, where reading it as "no opinion" would overwrite it. */
export function versionSeen(category: Scope3Category): number | null {
  return category.version ?? null;
}

/** Record a decision. **Never retried by the client**: a write whose outcome is unknown is a thing
 * to look at, and the page says so. Invalidates the inventory so the count, the sentence and the
 * row are what the server now says rather than a local patch - a locally-edited row beside a stale
 * count is the wrong-in-two-places failure `useDeclareNil` documents.
 *
 * **`expectedVersion` is required and has no default** (KD, 2026-10-24: the client sends what it
 * saw). A call that omits it does not compile, which is the point: `Review.tsx` never sent
 * `expectedVersion`, and the refusal EXP-34 built sat unreachable from the product for weeks. The
 * caller passes what *its reader* was looking at - `versionSeen(category)` at the moment the form
 * opened - and not whatever the inventory says by the time they submit. */
export function useDecideScope3Relevance() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (input: {
      category: number;
      financialYearEnd: string;
      relevance: DecidableRelevance;
      justification: string | null;
      expectedVersion: number | null;
      /** An Admin's reason, sent only on the second attempt after `period_closed` (ADR-0094). A
       * decision that already carries a justification needs none: the server uses it. */
      postCloseReason?: string;
    }): Promise<Scope3Decision> => {
      const response = await apiFetch(
        DECIDE_PATH.replace("{category_number}", String(input.category)),
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            financial_year_end: input.financialYearEnd,
            relevance: input.relevance,
            justification: input.justification,
            expected_version: input.expectedVersion,
            ...(input.postCloseReason ? { post_close_reason: input.postCloseReason } : {}),
          }),
        },
      );
      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) throw refusalFrom(response, body, "That decision could not be recorded.");
      return readDecision(body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}

// ---------------------------------------------------------------------- who decided, and when (S25)

/** What the row says about **who made the judgement** - four cases, and they are four because the
 * server's two fields can be null for four different reasons.
 *
 * A relevance decision is a person's judgement with a reason. Showing the verdict without its
 * author reduces it to a flag, which is the line `sources/census.py` draws for a declared nil. And
 * *no author* is not one fact: a category nobody has touched, a person since erased and a row
 * written with no person at all are three different statements, so they are not collapsed into a
 * blank.
 *
 * - `none`: no row. Nobody has acted on this category; nothing is said about anyone.
 * - `named`: somebody decided, and the page can say whether it was the reader. `withdrawn` is a
 *   decision taken back - the category is *Not assessed* again, and a person did that.
 * - `removed`: the date survives and the person does not (`ON DELETE SET NULL`, SEC-04).
 * - `unattributed`: a decision is on record with neither a person nor a date. Said plainly.
 */
export type Provenance =
  | { kind: "none" }
  | { kind: "named"; who: "you" | "a colleague"; at: string | null; withdrawn: boolean }
  | { kind: "removed"; at: string; withdrawn: boolean }
  | { kind: "unattributed" };

export function provenanceOf(category: Scope3Category, meId: string | undefined): Provenance {
  const by = category.decided_by ?? null;
  const at = category.decided_at ?? null;
  const withdrawn = category.relevance === "not_assessed";
  if (by === null) {
    if (at !== null) return { kind: "removed", at, withdrawn };
    return withdrawn ? { kind: "none" } : { kind: "unattributed" };
  }
  // **A name only where the reader may have one: *you*, or *a colleague*.** The roster is
  // Admin-only and an identifier is never printed (rule 4) - the Sources screen's rule for a
  // declared nil, kept here so the two screens describe a person the same way.
  return { kind: "named", who: by === meId ? "you" : "a colleague", at, withdrawn };
}

/** A timestamp as a reader can place it: the date, and the time with its zone - in the reader's own
 * zone (gate 30), or in UTC and labelled UTC while their preferences are not known. An instant that
 * cannot be read is handed back as it arrived, not made up. */
export function stamp(iso: string, reader?: Reader): string {
  return instantStamp(iso, reader);
}

// ------------------------------------------------------------- two people at once (S25)

/** The server's code for a first decision that lost to somebody else's, with a **different**
 * answer (`concurrent_decision`, HTTP 409). The same answer is not a refusal at all: it comes back
 * as `already_recorded`. */
export const DECISION_CONFLICT = "concurrent_decision" as const;

/** The server's code for a decision made against a row that **has since changed**
 * (`stale_decision`, HTTP 409): the reader opened the form on one version and a colleague's act
 * moved it. Its own code and not `stale_version`, because that one tells a screen to re-read a
 * document (ADR-0054, item 4).
 *
 * `already_recorded` is not this either: the *same* answer sent against a moved row is still a
 * repeat, which is what makes a retry after a lost response safe. */
export const DECISION_STALE = "stale_decision" as const;

/** Whether a write failed because somebody else's decision for this category is on record and is
 * not the one this reader saw - either because theirs landed first on an empty category
 * (`concurrent_decision`) or because the row moved after the form opened (`stale_decision`).
 *
 * A conflict is **not** a bad request (the form was right) and **not** an unknown outcome (we know
 * it did not land): the remedy is to look at what is on record and decide again if they still
 * disagree, which is a third thing neither `WriteFailed` branch says. The two codes are two facts
 * with one remedy, so they share this one question rather than two near-copies of a branch. */
export function isDecisionConflict(error: unknown): error is Refused {
  return (
    error instanceof Refused &&
    error.status === 409 &&
    (error.code === DECISION_CONFLICT || error.code === DECISION_STALE)
  );
}

// ------------------------------------------------------------ what suppliers say, rolled up

export type Scope3Rollup = components["schemas"]["Scope3RollupOut"];

/** The Scope 3 that suppliers' accepted answers account for, **summed on the server** and grouped by
 * company (`GET /supplier-exchange/scope3-rollup`).
 *
 * Additional information beside a filing and never added to a stated total (ADR-0167): whether a
 * regime states Scope 3 at all is regime data. The page prints the server's `total_kgco2e` and
 * `figure_count` as they come. Not issued without a window (`useInventory` says why).
 */
export function useScope3Rollup(
  window: { period_start: string; period_end: string } | undefined,
  method: "location" | "market",
) {
  return useQuery({
    queryKey: [
      "inventory",
      "scope3-rollup",
      window?.period_start ?? "none",
      window?.period_end ?? "none",
      method,
    ] as const,
    queryFn: window
      ? async (): Promise<Scope3Rollup> => {
          const { data, error, response } = await api.GET("/api/v1/supplier-exchange/scope3-rollup", {
            params: {
              query: {
                group: "entity",
                period_start: window.period_start,
                period_end: window.period_end,
                scope2_method: method,
              },
            },
          });
          if (error || !data) {
            throw refusalFrom(response, error, "What suppliers account for could not be read.");
          }
          return data;
        }
      : skipToken,
  });
}
